import React, { useMemo, useState } from 'react';
import {
	Alert,
	Button,
	Card,
	Col,
	Divider,
	Input,
	InputNumber,
	Popconfirm,
	Row,
	Switch,
	Tag,
	Tooltip,
	Typography,
} from 'antd';
import {
	CheckCircleFilled,
	ExperimentOutlined,
	FileSearchOutlined,
	MinusCircleOutlined,
	PlusOutlined,
} from '@ant-design/icons';
import { ml } from 'src/_shared/services/utils.js';
import { classifyFilename, BUILTIN_KEYWORDS, TASK_ORDER } from './classifyPreview.js';

const { Text } = Typography;

const TASK_LABELS = {
	employee: 'Employee feed',
	job: 'Job feed',
	candidate: 'Candidate feed',
	bonus: 'Bonus feed',
};

const TASK_DESCRIPTIONS = {
	employee: 'Roster imports — creates and updates employee account claims.',
	job: 'Open position imports — creates, updates, and closes jobs.',
	candidate: 'Applicant status imports — updates referral outcomes.',
	bonus: 'Bonus payment imports.',
};

/**
 * Feed state, derived from the pipeline cutover fields. The settings UX only
 * speaks "new pipeline": Active (live + confirmed), Validating (dry-run mode,
 * ERIN verifies deliveries before activating), or Off.
 */
export const feedState = (rule) => {
	if (rule?.pipelineMode === 'live' && rule?.dryRunConfirmation?.ledgerId) return 'active';
	if (rule?.pipelineMode === 'dryrun') return 'validating';
	return 'off';
};

const FEED_STATE_META = {
	active: { color: 'green', label: 'Active' },
	validating: { color: 'purple', label: 'Validating' },
	off: { color: 'default', label: 'Off' },
};

const FeedStateTag = ({ rule }) => {
	const state = feedState(rule);
	const meta = FEED_STATE_META[state];
	const confirmation = rule?.dryRunConfirmation;
	const tag = (
		<Tag
			color={meta.color}
			icon={state === 'active' ? <CheckCircleFilled /> : state === 'validating' ? <ExperimentOutlined /> : undefined}
			style={{ marginLeft: 8 }}
		>
			{meta.label}
		</Tag>
	);
	if (state === 'active' && confirmation?.confirmedAt) {
		const by = confirmation.confirmedBy ? ` · ${confirmation.confirmedBy}` : '';
		return (
			<Tooltip
				title={`Verified ${new Date(confirmation.confirmedAt).toLocaleDateString('en-US')}${by}`}
			>
				{tag}
			</Tooltip>
		);
	}

	return tag;
};

/** Chip list + inline add for include/exclude routing keys. */
const KeyChips = ({ label, hint, keys, color, onAdd, onRemove }) => {
	const [draft, setDraft] = useState('');
	const commit = () => {
		const value = draft.trim();
		if (value) onAdd(value);
		setDraft('');
	};

	return (
		<div style={{ marginBottom: 8 }}>
			<Text type="secondary" style={{ display: 'block', fontSize: 12, marginBottom: 4 }}>
				{label}
				{hint ? (
					<Tooltip title={hint}>
						<Text type="secondary" style={{ marginLeft: 4, cursor: 'help' }}>
							ⓘ
						</Text>
					</Tooltip>
				) : null}
			</Text>
			<div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 4 }}>
				{(keys || []).map((key) => (
					<Tag key={key} closable color={color} onClose={() => onRemove(key)}>
						{key}
					</Tag>
				))}
				<Input
					size="small"
					style={{ width: 180 }}
					placeholder="Add pattern…"
					value={draft}
					prefix={<PlusOutlined style={{ color: '#bbb' }} />}
					onChange={(e) => setDraft(e.target.value)}
					onPressEnter={commit}
					onBlur={commit}
				/>
			</div>
		</div>
	);
};

/** "Where would this file go?" — live preview of the routing rules. */
const FilenameTester = ({ sftpConfig }) => {
	const [filename, setFilename] = useState('');
	const result = useMemo(
		() => (filename.trim() ? classifyFilename(filename.trim(), sftpConfig) : undefined),
		[filename, sftpConfig]
	);

	return (
		<div style={{ background: '#fafafa', border: '1px solid #f0f0f0', borderRadius: 8, padding: 12 }}>
			<Text strong style={{ display: 'block', marginBottom: 6 }}>
				<FileSearchOutlined /> Test a filename
			</Text>
			<Input
				allowClear
				placeholder="e.g. ERIN_Employees_2026-07-15.csv"
				value={filename}
				onChange={(e) => setFilename(e.target.value)}
			/>
			{result === undefined ? (
				<Text type="secondary" style={{ display: 'block', marginTop: 6, fontSize: 12 }}>
					Type a filename to see which feed it would route to, using the rules above
					(unsaved changes included).
				</Text>
			) : result === null ? (
				<Alert
					showIcon
					style={{ marginTop: 8 }}
					type="warning"
					message="No feed matches — this file would be ignored."
					description="Add an include pattern to the intended feed, or rename the file to contain one of its keywords."
				/>
			) : (
				<Alert
					showIcon
					style={{ marginTop: 8 }}
					type="success"
					message={
						<span>
							Routes to <b>{TASK_LABELS[result.taskType]}</b>
							{' — '}
							{result.matchedBy === 'keyword' ? (
								<span>
									matches built-in keyword <Tag style={{ marginLeft: 4 }}>{result.match}</Tag>
								</span>
							) : (
								<span>
									matches include pattern <Tag color="blue" style={{ marginLeft: 4 }}>{result.match}</Tag>
								</span>
							)}
						</span>
					}
				/>
			)}
		</div>
	);
};

const SftpTaskRow = ({ taskType, rule, onChange, onActivate, canActivate, activatedBy }) => {
	const state = feedState(rule);
	const keywords = BUILTIN_KEYWORDS[taskType].join('", "');

	const setRule = (patch) => onChange(taskType, { ...rule, ...patch });

	const turnOn = () =>
		// Arming a feed starts it in validation mode: every delivery is
		// test-processed and reviewed before ERIN activates it for real writes.
		setRule({ enabled: true, pipelineMode: 'dryrun' });
	const turnOff = () => setRule({ enabled: false, pipelineMode: 'legacy' });
	// Support-admin shortcut: promote a validating feed straight to live. The
	// pipeline requires a recorded confirmation before it will mutate, so the
	// activation is stamped with who flipped it from here. onActivate SAVES the
	// flip immediately — no separate "Save Processing Settings" click (which was
	// easy to miss and silently left the feed in dry-run).
	const activate = () =>
		onActivate(taskType, {
			...rule,
			enabled: false,
			pipelineMode: 'live',
			dryRunConfirmation: {
				ledgerId: `manual-settings#${taskType}#${new Date().toISOString()}`,
				confirmedBy: `${activatedBy} — activated from company settings`,
				confirmedAt: new Date().toISOString(),
			},
		});

	return (
		<div style={{ padding: '12px 0' }}>
			<Row align="top" gutter={16}>
				<Col flex="220px">
					<div style={{ display: 'flex', alignItems: 'center' }}>
						{state === 'active' ? (
							<Popconfirm
								title="Turn off this feed? Incoming files will be ignored until it is re-activated."
								okText="Turn off"
								okButtonProps={{ danger: true }}
								onConfirm={turnOff}
							>
								<Switch checked size="small" />
							</Popconfirm>
						) : (
							<Switch
								checked={state !== 'off'}
								size="small"
								onChange={(checked) => (checked ? turnOn() : turnOff())}
							/>
						)}
						<Text strong style={{ marginLeft: 8 }}>
							{TASK_LABELS[taskType]}
						</Text>
						<FeedStateTag rule={rule} />
					</div>
					<Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 4 }}>
						{TASK_DESCRIPTIONS[taskType]}
					</Text>
				</Col>
				<Col flex="auto">
					{state === 'off' ? (
						<Text type="secondary" style={{ fontSize: 12 }}>
							Files matching “{keywords}” (or the patterns below) will be ignored while
							this feed is off.
						</Text>
					) : (
						<>
							{state === 'validating' ? (
								<Alert
									banner
									type="info"
									showIcon
									icon={<ExperimentOutlined />}
									style={{ marginBottom: 8, borderRadius: 6 }}
									message="Deliveries are being test-processed. ERIN verifies the results before activating live imports."
									action={
										canActivate ? (
											<Popconfirm
												title="Activate live imports? The next delivery will write to ERIN for real."
												okText="Activate"
												onConfirm={activate}
											>
												<Button size="small" type="primary" ghost>
													Activate now
												</Button>
											</Popconfirm>
										) : null
									}
								/>
							) : null}
							<KeyChips
								label="Route files containing"
								hint={`Files whose names contain “${keywords}” always route here. Add patterns for files that don't.`}
								keys={rule?.includeKeys}
								color="blue"
								onAdd={(key) => setRule({ includeKeys: [...(rule?.includeKeys || []), key] })}
								onRemove={(key) =>
									setRule({ includeKeys: (rule?.includeKeys || []).filter((k) => k !== key) })
								}
							/>
							<KeyChips
								label="Never route files containing"
								keys={rule?.excludeKeys}
								color="red"
								onAdd={(key) => setRule({ excludeKeys: [...(rule?.excludeKeys || []), key] })}
								onRemove={(key) =>
									setRule({ excludeKeys: (rule?.excludeKeys || []).filter((k) => k !== key) })
								}
							/>
							{taskType === 'employee' && rule?.schedule ? (
								<div style={{ marginTop: 4 }}>
									<Text type="secondary" style={{ fontSize: 12, marginRight: 8 }}>
										Pull schedule — fetch from remote SFTP every
									</Text>
									<InputNumber
										min={1}
										max={24}
										size="small"
										value={rule?.schedule?.every?.hours ?? 6}
										onChange={(hours) => setRule({ schedule: { every: { hours } } })}
									/>
									<Text type="secondary" style={{ fontSize: 12, marginLeft: 6 }}>
										hours
									</Text>
								</div>
							) : null}
						</>
					)}
				</Col>
			</Row>
		</div>
	);
};

const AtsTaskRow = ({ taskType, rule, onChange, newDisableType, setNewDisableType }) => {
	const setRule = (patch) => onChange(taskType, { ...rule, ...patch });

	return (
		<div style={{ padding: '12px 0' }}>
			<Row align="top" gutter={16}>
				<Col flex="220px">
					<div style={{ display: 'flex', alignItems: 'center' }}>
						<Switch
							checked={Boolean(rule?.enabled)}
							size="small"
							onChange={(enabled) => setRule({ enabled })}
						/>
						<Text strong style={{ marginLeft: 8, textTransform: 'capitalize' }}>
							{taskType} sync
						</Text>
					</div>
				</Col>
				<Col flex="auto">
					{rule?.enabled ? (
						<>
							<div style={{ marginBottom: taskType === 'job' ? 8 : 0 }}>
								<Text type="secondary" style={{ fontSize: 12, marginRight: 8 }}>
									Run every
								</Text>
								<InputNumber
									min={0}
									max={24}
									size="small"
									value={rule?.schedule?.every?.hours ?? 0}
									onChange={(hours) => setRule({ schedule: { every: { hours } } })}
								/>
								<Text type="secondary" style={{ fontSize: 12, marginLeft: 6 }}>
									hours
								</Text>
							</div>
							{taskType === 'job' ? (
								<div>
									<Text type="secondary" style={{ display: 'block', fontSize: 12, marginBottom: 4 }}>
										Skip these ATS types
									</Text>
									<div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 4 }}>
										{(rule?.disableATSType || []).map((atsType) => (
											<Tag
												key={atsType}
												closable
												icon={<MinusCircleOutlined />}
												onClose={() =>
													setRule({
														disableATSType: (rule?.disableATSType || []).filter(
															(t) => t !== atsType
														),
													})
												}
											>
												{atsType}
											</Tag>
										))}
										<Input
											size="small"
											style={{ width: 180 }}
											placeholder="Add ATS type…"
											value={newDisableType}
											onChange={(e) => setNewDisableType(e.target.value)}
											onPressEnter={() => {
												const value = newDisableType.trim();
												if (value)
													setRule({
														disableATSType: [...(rule?.disableATSType || []), value],
													});
												setNewDisableType('');
											}}
										/>
									</div>
								</div>
							) : null}
						</>
					) : (
						<Text type="secondary" style={{ fontSize: 12 }}>
							Scheduled {taskType} syncs from the connected ATS are off.
						</Text>
					)}
				</Col>
			</Row>
		</div>
	);
};

/**
 * File feeds (SFTP) + scheduled ATS processing, in the new-pipeline UX. All
 * cutover plumbing (enabled/pipelineMode) is written behind the scenes; the
 * page never mentions the retired legacy path.
 */
const ProcessingSettingsCard = ({
	currentUser,
	allMultiLingualData,
	sftpConfig,
	atsConfig,
	onSftpRuleChange,
	onAtsRuleChange,
	onActivate,
	canActivate,
	activatedBy,
	dirty,
	saving,
	onSave,
	saveResult,
}) => {
	const [newDisableType, setNewDisableType] = useState('');

	// No customer has used bonus feeds since early 2026 — hide the row unless a
	// company still carries real bonus config (two have stale debris).
	const bonusConfigured = (rule) =>
		Boolean(
			rule?.enabled ||
				rule?.pipelineMode === 'live' ||
				rule?.pipelineMode === 'dryrun' ||
				rule?.includeKeys?.length ||
				rule?.excludeKeys?.length
		);
	const sftpTasks = TASK_ORDER.filter(
		(task) => task !== 'bonus' || bonusConfigured(sftpConfig?.bonus)
	);
	const atsTasks = ['employee', 'job', 'candidate', 'bonus'].filter(
		(task) => task !== 'bonus' || atsConfig?.bonus?.enabled
	);

	return (
		<>
			<Card
				bordered={false}
				className="setting-card-inner"
				title={
					<div>
						{ml('File Feeds (SFTP)', currentUser, allMultiLingualData)}
						<Text type="secondary" style={{ display: 'block', fontSize: 13, fontWeight: 400 }}>
							Files delivered to your ERIN SFTP folder are routed to a feed by
							filename, then imported automatically.
						</Text>
					</div>
				}
			>
				{sftpTasks.map((taskType, index) => (
					<React.Fragment key={taskType}>
						{index > 0 ? <Divider style={{ margin: 0 }} /> : null}
						<SftpTaskRow
							taskType={taskType}
							rule={sftpConfig?.[taskType]}
							onChange={onSftpRuleChange}
							onActivate={onActivate}
							canActivate={canActivate}
							activatedBy={activatedBy}
						/>
					</React.Fragment>
				))}
				<FilenameTester sftpConfig={sftpConfig} />
			</Card>

			<Card
				bordered={false}
				className="setting-card-inner"
				title={
					<div>
						{ml('Scheduled ATS Processing', currentUser, allMultiLingualData)}
						<Text type="secondary" style={{ display: 'block', fontSize: 13, fontWeight: 400 }}>
							Recurring syncs pulled directly from your connected ATS.
						</Text>
					</div>
				}
			>
				{atsTasks.map((taskType, index) => (
					<React.Fragment key={taskType}>
						{index > 0 ? <Divider style={{ margin: 0 }} /> : null}
						<AtsTaskRow
							taskType={taskType}
							rule={atsConfig?.[taskType]}
							onChange={onAtsRuleChange}
							newDisableType={newDisableType}
							setNewDisableType={setNewDisableType}
						/>
					</React.Fragment>
				))}
			</Card>

			<div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 4 }}>
				<Button type="primary" size="large" disabled={!dirty} loading={saving} onClick={onSave}>
					{ml('Save Processing Settings', currentUser, allMultiLingualData)}
				</Button>
				{dirty ? <Text type="warning">Unsaved changes</Text> : null}
				{saveResult === 'success' ? (
					<Text type="success">Processing settings saved.</Text>
				) : null}
				{saveResult === 'error' ? (
					<Text type="danger">Could not save — try again.</Text>
				) : null}
			</div>
		</>
	);
};

export default ProcessingSettingsCard;
